import * as _ from 'underscore';
import { L10n } from '../L10n/L10n';
import { IDataSourceDetailInfo } from '../models/IDataSourceInfo';
import { Module } from '../Module';
import { DataSources } from '../resources/Datasources';
import { RemoteRefreshAgents } from '../resources/RemoteRefreshAgents';
import { IGetRemoteRefreshAgentsResult, IRemoteRefreshAgent } from '../ServerApi/requests/GetRemoteRefreshAgentsRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ToasterService, ToastType } from './ToasterService';

interface IRemoteQueryAgentEditModel {
  remoteAgentName: string;
}

interface IRemoteQueryAgentOption {
  display: string;
  remoteAgentName: string;
}

interface IChangeDatasourceRemoteQueryAgentScope extends ng.IScope {
  prompt: string;
  remoteQueryAgentOptions: IRemoteQueryAgentOption[];
  editModel: IRemoteQueryAgentEditModel;
}

export class ChangeDatasourceRemoteQueryAgentAction {

  public static $inject = [
    '$rootScope',
    '$q',
    '$translate',
    'ConfirmActionDialog',
    'RemoteRefreshAgents',
    'DataSources'
  ];

  constructor(
    private $rootScope: ng.IRootScopeService,
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private ConfirmActionDialog: ConfirmActionDialog,
    private RemoteRefreshAgents: RemoteRefreshAgents,
    private DataSources: DataSources) { }

  // Special option value: Do not associate any Bridge client and disable remoting live connections for this data source
  private UseDisabledOptionId: string = null;
  // Special option value: Associate this data source with whatever the site default is
  private UseDefaultOptionId: string = '$default$';

  public execute(datasource: IDataSourceDetailInfo): ng.IPromise<any> {

    const dialogScope = <IChangeDatasourceRemoteQueryAgentScope> this.$rootScope.$new();

    dialogScope.prompt = this.$translate.instant('editDatasourceRemoteQueryAgentDialogPrompt',
      { DATASOURCE_NAME: L10n.elideIfLong(datasource.name) });

    dialogScope.remoteQueryAgentOptions = [];

    if (datasource.remoteQueryAgentSiteDefault) {
      dialogScope.editModel = {remoteAgentName: this.UseDefaultOptionId};
    } else {
      dialogScope.editModel = {remoteAgentName: datasource.remoteAgentName};
    }

    this.populateRemoteQueryAgentOptions(datasource, dialogScope);

    return this.ConfirmActionDialog.open({
      scope: dialogScope,
      title: this.$translate.instant('editDatasourceRemoteQueryAgentDialogTitle'),
      bodyTemplateUrl: 'confirmActionDialogDatasourceRemoteQueryAgent.html',
      size: '400px',
      actionName: this.$translate.instant('save'),
      action: angular.noop,
    }).result
      .then((result: IResult) => {
        return this.assignDataSourceToRemoteQueryAgent(datasource.id, dialogScope);
      })
      .finally(() => {
        dialogScope.$destroy();
      });
  }

  private assignDataSourceToRemoteQueryAgent(datasourceId: string, scope: IChangeDatasourceRemoteQueryAgentScope):
                                                  ng.IPromise<IResult> {

    const useDefaultAgent = this.UseDefaultOptionId === scope.editModel.remoteAgentName;
    const remoteAgentName = useDefaultAgent? null : scope.editModel.remoteAgentName;

    return this.DataSources.setRemoteQueryAgent(datasourceId, remoteAgentName, useDefaultAgent)
      .then((result: IResult) => {
        if (result.errors && result.errors.length) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: this.$translate.instant('errorUnknown')});
        } else {
          ToasterService.instance.pop({
            type: ToastType.Info,
            text: this.$translate.instant('editDatasourceRemoteQueryAgentSuccess')
          });
        }

        return result;
    });
  }

  private populateRemoteQueryAgentOptions(datasource: IDataSourceDetailInfo, scope: IChangeDatasourceRemoteQueryAgentScope) {
    return this.RemoteRefreshAgents.get()
      .then((result: IGetRemoteRefreshAgentsResult) => {
        scope.remoteQueryAgentOptions = this.buildRemoteQueryAgentsList(result, datasource);

        // Add the option for reverting to the site default
        scope.remoteQueryAgentOptions.unshift({
          display: this.$translate.instant('datasourceRemoteQueryAgentSiteDefaultOption'),
          remoteAgentName: this.UseDefaultOptionId
        });

        // Add the option for assigning no live sync client & disabling remoting
        scope.remoteQueryAgentOptions.unshift({
          display: this.$translate.instant('datasourceRemoteQueryAgentDisabledOption'),
          remoteAgentName: this.UseDisabledOptionId
        });

        // Select default option when site default is used
        if (datasource.remoteQueryAgentSiteDefault) {
          scope.editModel.remoteAgentName = this.UseDefaultOptionId;
        }
        // Select default option when site default is used (but no site default agent is assigned)
        else if (!datasource.remoteAgentName && !datasource.testingConnectionAllowed) {
          scope.editModel.remoteAgentName = this.UseDefaultOptionId;
        }
        // Select default option when remoting live connections is off
        else if (!datasource.remoteAgentName) {
          scope.editModel.remoteAgentName = this.UseDisabledOptionId;
        }
      });
  }

  // Build a list of agents, it should include
  // 1. all agents returned from server side
  // 2. current assigned agent, it may not be returned by server
  private buildRemoteQueryAgentsList(remoteAgentResult: IGetRemoteRefreshAgentsResult, datasource: IDataSourceDetailInfo): IRemoteQueryAgentOption[] {
    let listContainsExistingAgent = false;

    const agentList: IRemoteQueryAgentOption[] = _.map(remoteAgentResult.agents, (agent: IRemoteRefreshAgent) => {
      if (datasource.remoteAgentName && datasource.remoteAgentName === agent.name) {
        listContainsExistingAgent = true;
      }

      return { display: agent.name, remoteAgentName: agent.name };
    });

    // Add current agent if not listed by Vizportal
    if (!listContainsExistingAgent && datasource && datasource.remoteAgentName) {
      agentList.unshift({ display: datasource.remoteAgentName, remoteAgentName: datasource.remoteAgentName });
    }

    return agentList;
  }
}

Module.instance().service('ChangeDatasourceRemoteQueryAgentAction', ChangeDatasourceRemoteQueryAgentAction);
